/**
 * Task #1237 — Tab DB Quotas dentro StorageMaster.
 * Tabella tenant con quota DB, % utilizzo, status, azioni:
 *  - Recalc on-demand
 *  - Modifica quota max bytes
 *  - Sblocco emergenza 24h
 *  - Hard-block manuale (DB | R2 | both)
 */
import { useState } from 'react';
import { useMasterStorageQuota, formatBytes, type MasterTenantQuotaRow } from '@/hooks/useMasterStorageQuota';
import { apiClient } from '@/lib/api';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Unlock, ShieldAlert, Edit2 } from 'lucide-react';
import { toErr } from '@easyflow/shared';

const STATUS_BADGE: Record<string, JSX.Element> = {
  ok: <Badge className="bg-green-500/20 text-green-400 border-green-500/30">OK</Badge>,
  info: <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Info</Badge>,
  warning: <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Warning</Badge>,
  critical: <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Critico</Badge>,
  grace: <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Grace 100%</Badge>,
  blocked: <Badge variant="destructive">BLOCCATO</Badge>,
};

export default function DBQuotasTab(): JSX.Element {
  const { rows, loading, blockedCount, refresh } = useMasterStorageQuota();
  const [busy, setBusy] = useState<string | null>(null);
  const { toast } = useToast();

  const recalc = async (tid: string): Promise<void> => {
    setBusy(tid);
    try {
      await apiClient.post(`/master/storage/db/tenants/${tid}/recalc`, {});
      await refresh();
      toast({ title: 'Ricalcolo completato' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore ricalcolo', description: __e?.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const editQuota = async (row: MasterTenantQuotaRow): Promise<void> => {
    const v = window.prompt(`Nuova quota DB in GB per ${row.tenantName}:`, String(Math.round(row.dbMaxBytes / 1024 / 1024 / 1024)));
    if (!v) return;
    const gb = parseFloat(v);
    if (!Number.isFinite(gb) || gb <= 0) return;
    setBusy(row.tenantId);
    try {
      await apiClient.put(`/master/storage/db/tenants/${row.tenantId}/quota`, { dbMaxBytes: Math.floor(gb * 1024 * 1024 * 1024) });
      await refresh();
      toast({ title: 'Quota aggiornata' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore aggiornamento', description: __e?.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const unblock = async (row: MasterTenantQuotaRow): Promise<void> => {
    // Round 5 fix #1 — motivazione obbligatoria, no fallback default. Cancel
    // o input vuoto/troppo corto abortisce l'azione (audit log richiede reason esplicita).
    const raw = window.prompt(`Motivo sblocco emergenza 24h per ${row.tenantName} (min 5 caratteri):`, '');
    if (raw === null) return; // utente ha premuto Cancel
    const reason = raw.trim();
    if (reason.length < 5) {
      toast({ title: 'Motivazione obbligatoria', description: 'Inserisci almeno 5 caratteri per giustificare lo sblocco di emergenza.', variant: 'destructive' });
      return;
    }
    setBusy(row.tenantId);
    try {
      await apiClient.post(`/master/storage/db/tenants/${row.tenantId}/unblock`, { hours: 24, reason });
      await refresh();
      toast({ title: 'Sblocco emergenza attivato (24h)' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore sblocco', description: __e?.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const block = async (row: MasterTenantQuotaRow, scope: 'db' | 'r2' | 'both'): Promise<void> => {
    if (!window.confirm(`Hard-block ${scope.toUpperCase()} per ${row.tenantName}?`)) return;
    setBusy(row.tenantId);
    try {
      await apiClient.post(`/master/storage/db/tenants/${row.tenantId}/block`, { scope });
      await refresh();
      toast({ title: `Block ${scope} attivato` });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore block', description: __e?.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Quote Database per Tenant</h3>
          <p className="text-sm text-muted-foreground">
            {rows.length} tenant — {blockedCount} in hard-block
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-2" />Aggiorna
        </Button>
      </div>
      <div className="overflow-x-auto rounded border border-border dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2">Tenant</th>
              <th className="text-right p-2">DB</th>
              <th className="text-right p-2">%</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Last calc</th>
              <th className="text-left p-2">Emergency</th>
              <th className="text-right p-2">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.tenantId} className="border-t border-border dark:border-slate-700">
                <td className="p-2">{row.tenantName}</td>
                <td className="p-2 text-right">
                  {formatBytes(row.dbUsedBytes)} / {formatBytes(row.dbMaxBytes)}
                </td>
                <td className="p-2 text-right font-mono">{row.dbPct.toFixed(1)}%</td>
                <td className="p-2">{STATUS_BADGE[row.status] || row.status}</td>
                <td className="p-2 text-xs text-muted-foreground">
                  {row.lastCalcAt ? new Date(row.lastCalcAt).toLocaleString('it-IT') : '—'}
                </td>
                <td className="p-2 text-xs">
                  {row.emergencyUnblockUntil
                    ? <span className="text-emerald-600">fino {new Date(row.emergencyUnblockUntil).toLocaleString('it-IT')}</span>
                    : '—'}
                </td>
                <td className="p-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" disabled={busy === row.tenantId}
                    onClick={() => void recalc(row.tenantId)} title="Ricalcola byte DB">
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === row.tenantId}
                    onClick={() => void editQuota(row)} title="Modifica quota">
                    <Edit2 className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === row.tenantId}
                    onClick={() => void unblock(row)} title="Sblocco emergenza 24h">
                    <Unlock className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === row.tenantId}
                    onClick={() => void block(row, 'db')} title="Hard-block DB">
                    <ShieldAlert className="h-3 w-3 text-red-600" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                Nessun tenant — verifica che la migration #1237 sia applicata.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
